/**
 * installTintHover — ONE global tint-wash hover for every button, all editors.
 *
 * Why JS and not pure CSS: most buttons here set `box-shadow` inline (glows, drop
 * shadows, or even `none`), and inline styles beat a stylesheet `:hover` rule — so
 * a CSS `button:hover { box-shadow }` never reaches them. This delegated handler
 * instead APPENDS the tint inset to whatever inline box-shadow a button already
 * has (preserving glows/shadows) and restores it on leave, so it works regardless
 * of how the button is styled.
 *
 * A single capture-phase listener pair on `document` covers every button in every
 * editor (one SPA, one document). Opt a button out with `data-no-tint-hover`.
 */

const TINT = 'inset 0 0 0 999px var(--btn-hover)';
const SAVED = 'tintHoverPrev'; // dataset key holding the pre-hover inline box-shadow

function buttonOf(node: EventTarget | null): HTMLElement | null {
  const el = node as HTMLElement | null;
  if (!el || !el.closest) return null;
  return el.closest('button, [role="button"]') as HTMLElement | null;
}

export function installTintHover(): () => void {
  const onOver = (e: PointerEvent) => {
    const btn = buttonOf(e.target);
    if (!btn) return;
    if (btn.contains(e.relatedTarget as Node)) return; // moving within the button
    if ((btn as HTMLButtonElement).disabled || btn.getAttribute('aria-disabled') === 'true') return;
    if (btn.hasAttribute('data-no-tint-hover')) return;
    if (SAVED in btn.dataset) return; // already washed
    const prev = btn.style.boxShadow;
    btn.dataset[SAVED] = prev;
    // Destructive buttons (Clear) wash red instead of the tint, same mechanism.
    const wash = btn.hasAttribute('data-hover-danger')
      ? 'inset 0 0 0 999px var(--btn-hover-danger)'
      : TINT;
    // `none` can't be combined with other shadows (invalid CSS → whole value
    // dropped), so only prepend when there's a real existing shadow.
    btn.style.boxShadow = prev && prev !== 'none' ? `${prev}, ${wash}` : wash;
  };

  const onOut = (e: PointerEvent) => {
    const btn = buttonOf(e.target);
    if (!btn) return;
    if (btn.contains(e.relatedTarget as Node)) return; // still inside the button
    if (!(SAVED in btn.dataset)) return;
    btn.style.boxShadow = btn.dataset[SAVED] || '';
    delete btn.dataset[SAVED];
  };

  document.addEventListener('pointerover', onOver, true);
  document.addEventListener('pointerout', onOut, true);
  return () => {
    document.removeEventListener('pointerover', onOver, true);
    document.removeEventListener('pointerout', onOut, true);
  };
}
